/**
 * OfferPreviewApiPage (FS-237) — Trackier-style partner-key management.
 *
 * Layout (top → bottom):
 *   1. Topbar    — breadcrumb + H1 + "Create API Key" CTA (green).
 *   2. Filter    — search input + status segmented control (all/active/revoked).
 *   3. Table     — Name / Token / Usage / Created + Revoke action; loading, empty, error branches.
 *   4. Drawer    — slide-in form: label + scopes + generate; on success shows the raw key ONCE.
 *
 * Brand: white canvas, zinc-200 borders, green-600/700/50 accents only,
 * Inter font (inherited from app root), 6-8px rounding, inline SVG icons.
 * No emoji, no dark mode, no competitor product names.
 */

import { useMemo, useState, type FormEvent } from "react";
import {
	AVAILABLE_SCOPES,
	DEFAULT_SCOPE,
	filterKeys,
	formatLastUsed,
	formatUnixSec,
	keyStatus,
	maskToken,
	type KeyStatus,
	type PartnerKeyMasked,
} from "./api";
import {
	useOfferPreviewApiCreate,
	useOfferPreviewApiDelete,
	useOfferPreviewApiList,
} from "./hooks";

// ---------------------------------------------------------------------------
// Design tokens (green-only accent per CLAUDE.md)
// ---------------------------------------------------------------------------

const CARD = "rounded-[8px] border border-zinc-200 bg-white shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:bg-zinc-50";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-600 bg-green-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-300 bg-white px-3.5 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER_GHOST =
	"inline-flex items-center gap-1 rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:border-red-300 hover:text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type StatusFilter = "all" | KeyStatus;

export function OfferPreviewApiPage() {
	const listQuery = useOfferPreviewApiList();
	const createMut = useOfferPreviewApiCreate();
	const revokeMut = useOfferPreviewApiDelete();

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState<StatusFilter>("all");
	const [revealed, setRevealed] = useState<{
		raw: string;
		label: string | null;
	} | null>(null);

	const rows = listQuery.data?.keys ?? [];
	const filtered = useMemo(
		() => filterKeys(rows, search, status),
		[rows, search, status],
	);

	const activeCount = rows.filter((r) => keyStatus(r) === "active").length;
	const revokedCount = rows.length - activeCount;

	function openDrawer() {
		setDrawerOpen(true);
		createMut.reset();
	}

	function closeDrawer() {
		setDrawerOpen(false);
	}

	return (
		<main
			data-testid="offer-preview-api-page"
			className="flex-1 overflow-auto bg-zinc-50/30"
		>
			<div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6">
				<Topbar onCreate={openDrawer} />

				<Filters
					search={search}
					onSearch={setSearch}
					status={status}
					onStatus={setStatus}
					total={rows.length}
					activeCount={activeCount}
					revokedCount={revokedCount}
				/>

				<KeysTable
					query={listQuery}
					filtered={filtered}
					onRevoke={(id) => revokeMut.mutate(id)}
					revokingId={
						revokeMut.isPending ? (revokeMut.variables ?? null) : null
					}
				/>
			</div>

			{drawerOpen ? (
				<CreateDrawer
					onClose={closeDrawer}
					onSubmit={(body) => {
						createMut.mutate(body, {
							onSuccess: (res) => {
								setRevealed({ raw: res.raw, label: res.key.label });
								closeDrawer();
							},
						});
					}}
					isPending={createMut.isPending}
					error={createMut.error?.message ?? null}
				/>
			) : null}

			{revealed ? (
				<RevealModal
					raw={revealed.raw}
					label={revealed.label}
					onClose={() => setRevealed(null)}
				/>
			) : null}
		</main>
	);
}

export default OfferPreviewApiPage;

// ---------------------------------------------------------------------------
// Topbar
// ---------------------------------------------------------------------------

function Topbar(props: { onCreate: () => void }) {
	return (
		<header
			className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
			data-testid="offer-preview-api-topbar"
		>
			<div className="flex flex-col gap-2">
				<nav
					aria-label="Breadcrumb"
					className="flex items-center gap-2 text-sm text-zinc-500"
				>
					<span>Settings</span>
					<span aria-hidden="true" className="text-zinc-300">
						/
					</span>
					<span className="font-medium text-zinc-900">Offer Preview API</span>
				</nav>
				<h1 className="text-2xl font-semibold bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-transparent">
					Offer Preview API
				</h1>
				<p className="max-w-2xl text-sm text-zinc-500">
					Partner-facing read-only offer metadata endpoint. Issue scoped API
					keys to ad networks and traffic partners, then they call
					<code className="mx-1 rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-xs text-zinc-700">
						GET /offers/preview/:id
					</code>
					with an <code className="font-mono text-xs">x-partner-api-key</code>{" "}
					header.
				</p>
			</div>
			<div className="flex shrink-0 items-center gap-2">
				<button
					type="button"
					className={BTN_PRIMARY}
					onClick={props.onCreate}
					data-testid="offer-preview-api-create-button"
				>
					<PlusIcon />
					Create API Key
				</button>
			</div>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

function Filters(props: {
	search: string;
	onSearch: (v: string) => void;
	status: StatusFilter;
	onStatus: (v: StatusFilter) => void;
	total: number;
	activeCount: number;
	revokedCount: number;
}) {
	const tabs: Array<{ id: StatusFilter; label: string; count: number }> = [
		{ id: "all", label: "All", count: props.total },
		{ id: "active", label: "Active", count: props.activeCount },
		{ id: "revoked", label: "Revoked", count: props.revokedCount },
	];
	return (
		<section
			className={`${CARD} p-4`}
			aria-label="Filters"
			data-testid="offer-preview-api-filters"
		>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="relative w-full sm:max-w-sm">
					<span
						aria-hidden="true"
						className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
					>
						<SearchIcon />
					</span>
					<input
						type="search"
						value={props.search}
						onChange={(e) => props.onSearch(e.currentTarget.value)}
						placeholder="Search by label, prefix or id"
						className={`${INPUT} pl-9`}
						data-testid="offer-preview-api-search-input"
						aria-label="Search keys"
					/>
				</div>
				<div
					role="tablist"
					aria-label="Status filter"
					className="inline-flex rounded-[6px] border border-zinc-300 bg-white p-0.5"
				>
					{tabs.map((t) => {
						const active = props.status === t.id;
						return (
							<button
								key={t.id}
								type="button"
								role="tab"
								aria-selected={active}
								className={`rounded-[4px] px-3 py-1 text-xs font-medium transition-colors ${
									active
										? "bg-green-50 text-green-700"
										: "text-zinc-600 hover:bg-zinc-50"
								}`}
								onClick={() => props.onStatus(t.id)}
								data-testid={`offer-preview-api-status-tab-${t.id}`}
							>
								{t.label}
								<span
									className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
										active
											? "bg-green-600 text-white"
											: "bg-zinc-100 text-zinc-600"
									}`}
								>
									{t.count}
								</span>
							</button>
						);
					})}
				</div>
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function KeysTable(props: {
	query: ReturnType<typeof useOfferPreviewApiList>;
	filtered: PartnerKeyMasked[];
	onRevoke: (id: string) => void;
	revokingId: string | null;
}) {
	const { query, filtered } = props;

	if (query.isLoading) {
		return (
			<section className={CARD} aria-busy="true">
				<TableHeader />
				<div
					className="flex items-center justify-center px-6 py-16 text-sm text-zinc-500"
					data-testid="offer-preview-api-loading"
				>
					<Spinner />
					<span className="ml-2">Loading API keys.</span>
				</div>
			</section>
		);
	}

	if (query.error) {
		return (
			<section
				className={`${CARD} border-red-200`}
				role="alert"
				data-testid="offer-preview-api-error"
			>
				<TableHeader />
				<div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
					<WarnIcon />
					<p className="text-sm font-medium text-red-700">
						Unable to load API keys
					</p>
					<p className="text-xs text-zinc-600">{query.error.message}</p>
					<button
						type="button"
						className={`${BTN_SECONDARY} mt-2`}
						onClick={() => query.refetch()}
						data-testid="offer-preview-api-retry"
					>
						Retry
					</button>
				</div>
			</section>
		);
	}

	if (filtered.length === 0) {
		return (
			<section className={CARD} data-testid="offer-preview-api-empty">
				<TableHeader />
				<div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
					<KeyIcon />
					<p className="text-sm font-medium text-zinc-900">
						No API keys yet
					</p>
					<p className="max-w-md text-xs text-zinc-500">
						Mint your first partner key to let external systems pull offer
						metadata. The raw value is shown exactly once on creation.
					</p>
				</div>
			</section>
		);
	}

	return (
		<section className={CARD}>
			<TableHeader />
			<div className="overflow-x-auto">
				<table className="w-full border-collapse text-sm">
					<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
						<tr>
							<th className="px-4 py-3 text-left font-medium">Name</th>
							<th className="px-4 py-3 text-left font-medium">Token</th>
							<th className="px-4 py-3 text-left font-medium">Usage</th>
							<th className="px-4 py-3 text-left font-medium">Created</th>
							<th className="px-4 py-3 text-right font-medium">Actions</th>
						</tr>
					</thead>
					<tbody
						className="divide-y divide-zinc-200"
						data-testid="offer-preview-api-list"
					>
						{filtered.map((row) => (
							<Row
								key={row.id}
								row={row}
								onRevoke={props.onRevoke}
								revoking={props.revokingId === row.id}
							/>
						))}
					</tbody>
				</table>
			</div>
		</section>
	);
}

function TableHeader() {
	return (
		<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
			<div className="flex items-center gap-2">
				<KeyIcon />
				<h2 className="text-sm font-semibold text-zinc-900">
					Partner API Keys
				</h2>
			</div>
		</div>
	);
}

function Row(props: {
	row: PartnerKeyMasked;
	onRevoke: (id: string) => void;
	revoking: boolean;
}) {
	const row = props.row;
	const status = keyStatus(row);
	const isActive = status === "active";
	return (
		<tr
			className="hover:bg-green-50/30"
			data-testid={`offer-preview-api-row-${row.id}`}
		>
			<td className="px-4 py-3 align-top">
				<div className="flex flex-col gap-1">
					<span className="font-medium text-zinc-900">
						{row.label ?? <span className="text-zinc-400">Unnamed key</span>}
					</span>
					<div className="flex flex-wrap gap-1">
						{row.scopes.map((s) => (
							<ScopeBadge key={s}>{s}</ScopeBadge>
						))}
					</div>
				</div>
			</td>
			<td className="px-4 py-3 align-top">
				<div className="flex items-center gap-2">
					<code className="rounded border border-zinc-200 bg-zinc-50 px-2 py-1 font-mono text-xs text-zinc-800">
						{maskToken(row.key_prefix)}
					</code>
					<StatusBadge status={status} />
				</div>
			</td>
			<td className="px-4 py-3 align-top text-xs text-zinc-600">
				<div className="flex flex-col gap-0.5">
					<span
						className={isActive ? "text-zinc-900" : "text-zinc-400"}
						data-testid={`offer-preview-api-last-used-${row.id}`}
					>
						{formatLastUsed(row.last_used_at)}
					</span>
					{row.last_used_at !== null ? (
						<span className="text-[11px] text-zinc-400">
							{formatUnixSec(row.last_used_at)}
						</span>
					) : null}
				</div>
			</td>
			<td className="px-4 py-3 align-top text-xs text-zinc-600">
				{formatUnixSec(row.created_at)}
			</td>
			<td className="px-4 py-3 align-top text-right">
				{isActive ? (
					<button
						type="button"
						className={BTN_DANGER_GHOST}
						onClick={() => props.onRevoke(row.id)}
						disabled={props.revoking}
						data-testid={`offer-preview-api-revoke-${row.id}`}
					>
						{props.revoking ? "Revoking." : "Revoke"}
					</button>
				) : (
					<span className="text-xs text-zinc-400">—</span>
				)}
			</td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Create drawer
// ---------------------------------------------------------------------------

function CreateDrawer(props: {
	onClose: () => void;
	onSubmit: (body: { label: string | null; scopes: string[] }) => void;
	isPending: boolean;
	error: string | null;
}) {
	const [label, setLabel] = useState("");
	const [scopes, setScopes] = useState<string[]>([DEFAULT_SCOPE]);

	function toggleScope(scope: string) {
		setScopes((prev) =>
			prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
		);
	}

	function onSubmit(e: FormEvent) {
		e.preventDefault();
		if (scopes.length === 0) return;
		props.onSubmit({ label: label.trim() || null, scopes });
	}

	return (
		<div
			className="fixed inset-0 z-40 flex justify-end"
			role="dialog"
			aria-modal="true"
			aria-labelledby="offer-preview-api-drawer-title"
			data-testid="offer-preview-api-drawer"
		>
			<button
				type="button"
				aria-label="Close drawer"
				className="absolute inset-0 bg-zinc-900/30"
				onClick={props.onClose}
			/>
			<form
				onSubmit={onSubmit}
				className="relative flex h-full w-full max-w-md flex-col border-l border-zinc-200 bg-white shadow-xl"
				data-testid="offer-preview-api-create-form"
			>
				<header className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
					<div className="flex flex-col gap-0.5">
						<h2
							id="offer-preview-api-drawer-title"
							className="text-base font-semibold text-zinc-900"
						>
							Create API Key
						</h2>
						<p className="text-xs text-zinc-500">
							Scoped, tenant-bound, SHA-256 hashed at rest.
						</p>
					</div>
					<button
						type="button"
						aria-label="Close"
						className="rounded-[6px] p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
						onClick={props.onClose}
					>
						<CloseIcon />
					</button>
				</header>

				<div className="flex-1 overflow-y-auto px-5 py-5">
					<div className="flex flex-col gap-5">
						<label className="flex flex-col gap-1.5">
							<span className="text-sm font-medium text-zinc-700">
								Label
							</span>
							<input
								type="text"
								value={label}
								onChange={(e) => setLabel(e.currentTarget.value)}
								placeholder="e.g. Acme Ad Network"
								className={INPUT}
								disabled={props.isPending}
								data-testid="offer-preview-api-label-input"
								autoFocus
							/>
							<span className="text-xs text-zinc-500">
								Optional. Shown in the table and audit log.
							</span>
						</label>

						<fieldset className="flex flex-col gap-2">
							<legend className="text-sm font-medium text-zinc-700">
								Scopes
							</legend>
							<p className="text-xs text-zinc-500">
								At least one scope is required. Partner requests are rejected
								if the scope is missing.
							</p>
							<div className="mt-1 flex flex-col gap-2">
								{AVAILABLE_SCOPES.map((scope) => {
									const checked = scopes.includes(scope);
									return (
										<label
											key={scope}
											className={`flex items-center gap-2 rounded-[6px] border px-3 py-2 text-sm cursor-pointer ${
												checked
													? "border-green-600 bg-green-50"
													: "border-zinc-300 bg-white hover:bg-zinc-50"
											}`}
										>
											<input
												type="checkbox"
												checked={checked}
												onChange={() => toggleScope(scope)}
												disabled={props.isPending}
												className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600"
												data-testid={`offer-preview-api-scope-${scope}`}
											/>
											<code className="font-mono text-xs text-zinc-800">
												{scope}
											</code>
										</label>
									);
								})}
							</div>
						</fieldset>

						{props.error ? (
							<div
								role="alert"
								className="rounded-[6px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
								data-testid="offer-preview-api-form-error"
							>
								{props.error}
							</div>
						) : null}
					</div>
				</div>

				<footer className="flex items-center justify-end gap-2 border-t border-zinc-200 px-5 py-4">
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={props.onClose}
						disabled={props.isPending}
					>
						Cancel
					</button>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={props.isPending || scopes.length === 0}
						data-testid="offer-preview-api-submit"
					>
						{props.isPending ? (
							<>
								<Spinner />
								Generating.
							</>
						) : (
							"Generate Key"
						)}
					</button>
				</footer>
			</form>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Reveal modal (raw key shown ONCE)
// ---------------------------------------------------------------------------

function RevealModal(props: {
	raw: string;
	label: string | null;
	onClose: () => void;
}) {
	const [copied, setCopied] = useState(false);

	async function copy() {
		try {
			await navigator.clipboard.writeText(props.raw);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			/* clipboard unavailable — user can still select manually */
		}
	}

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
			role="dialog"
			aria-modal="true"
			aria-labelledby="offer-preview-api-reveal-title"
			data-testid="offer-preview-api-reveal"
		>
			<div className="absolute inset-0 bg-zinc-900/40" />
			<div className="relative w-full max-w-lg rounded-[8px] border border-zinc-200 bg-white shadow-xl">
				<header className="flex items-start gap-3 border-b border-zinc-200 px-5 py-4">
					<span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-green-50 text-green-700">
						<KeyIcon />
					</span>
					<div className="flex flex-col gap-0.5">
						<h2
							id="offer-preview-api-reveal-title"
							className="text-base font-semibold text-zinc-900"
						>
							API Key created
						</h2>
						<p className="text-xs text-zinc-500">
							{props.label ? `"${props.label}" — ` : ""}Copy this value now — it
							will not be shown again.
						</p>
					</div>
				</header>
				<div className="px-5 py-5">
					<div className="flex items-stretch gap-2">
						<code
							className="flex-1 break-all rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2 font-mono text-xs text-zinc-800"
							data-testid="offer-preview-api-reveal-value"
						>
							{props.raw}
						</code>
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={copy}
							data-testid="offer-preview-api-copy"
						>
							{copied ? "Copied" : "Copy"}
						</button>
					</div>
					<p className="mt-3 rounded-[6px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
						Treat this like a password. Store it in your partner's vault and
						never commit it to source control.
					</p>
				</div>
				<footer className="flex justify-end border-t border-zinc-200 px-5 py-3">
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={props.onClose}
						data-testid="offer-preview-api-reveal-close"
					>
						I have stored it
					</button>
				</footer>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Small UI atoms
// ---------------------------------------------------------------------------

function ScopeBadge({ children }: { children: React.ReactNode }) {
	return (
		<span className="inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2 py-0.5 font-mono text-[10px] font-medium text-green-700">
			{children}
		</span>
	);
}

function StatusBadge(props: { status: KeyStatus }) {
	const active = props.status === "active";
	return (
		<span
			className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
				active
					? "bg-green-50 text-green-700 border border-green-200"
					: "bg-zinc-100 text-zinc-600 border border-zinc-200"
			}`}
			data-testid={`offer-preview-api-status-${props.status}`}
		>
			<span
				aria-hidden="true"
				className={`inline-block h-1.5 w-1.5 rounded-full ${
					active ? "bg-green-600" : "bg-zinc-400"
				}`}
			/>
			{active ? "Active" : "Revoked"}
		</span>
	);
}

function Spinner() {
	return (
		<span
			aria-hidden="true"
			className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-zinc-300 border-t-green-600"
		/>
	);
}

function PlusIcon() {
	return (
		<svg
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			className="h-4 w-4"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M10 4v12M4 10h12" />
		</svg>
	);
}

function SearchIcon() {
	return (
		<svg
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			className="h-4 w-4"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="9" cy="9" r="5.5" />
			<path d="M13 13l3 3" />
		</svg>
	);
}

function CloseIcon() {
	return (
		<svg
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			className="h-4 w-4"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M5 5l10 10M15 5L5 15" />
		</svg>
	);
}

function KeyIcon() {
	return (
		<svg
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			className="h-4 w-4"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="7" cy="13" r="3.5" />
			<path d="M9.5 10.5L17 3" />
			<path d="M14 6l2 2" />
		</svg>
	);
}

function WarnIcon() {
	return (
		<svg
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			className="h-6 w-6 text-red-500"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M10 3L2 17h16L10 3z" />
			<path d="M10 8v4" />
			<circle cx="10" cy="14.5" r="0.5" fill="currentColor" />
		</svg>
	);
}
